"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Clarity() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const annotations = [{
    label: "COMPLIANCE 100%",
    top: "14%",
    side: "left"
  }, {
    label: "STRATEGIC",
    top: "44%",
    side: "right"
  }, {
    label: "TAX OPTIMIZED",
    top: "72%",
    side: "left"
  }];
  return <section data-section-id="3263" id="clarity-in-every-filing" className="relative min-h-[92vh] w-full overflow-hidden bg-background text-foreground dark">
      {}
      <div className="pointer-events-none absolute top-0 right-0 h-[420px] w-[420px] opacity-[0.08]">
        <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M200 50 Q210 120 220 180 Q230 240 200 320" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <path d="M200 90 Q160 100 130 130" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <path d="M200 90 Q240 100 270 130" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <path d="M210 140 Q180 150 150 175" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <path d="M215 140 Q250 150 285 175" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <path d="M218 200 Q190 215 165 240" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <path d="M222 200 Q255 215 290 240" stroke="var(--foreground)" strokeWidth="1" fill="none" />
          <ellipse cx="150" cy="130" rx="22" ry="8" stroke="var(--foreground)" strokeWidth="1" fill="none" transform="rotate(-30 150 130)" />
          <ellipse cx="250" cy="130" rx="22" ry="8" stroke="var(--foreground)" strokeWidth="1" fill="none" transform="rotate(30 250 130)" />
          <ellipse cx="170" cy="175" rx="22" ry="8" stroke="var(--foreground)" strokeWidth="1" fill="none" transform="rotate(-25 170 175)" />
          <ellipse cx="265" cy="175" rx="22" ry="8" stroke="var(--foreground)" strokeWidth="1" fill="none" transform="rotate(25 265 175)" />
          <ellipse cx="185" cy="240" rx="22" ry="8" stroke="var(--foreground)" strokeWidth="1" fill="none" transform="rotate(-20 185 240)" />
          <ellipse cx="275" cy="240" rx="22" ry="8" stroke="var(--foreground)" strokeWidth="1" fill="none" transform="rotate(20 275 240)" />
        </svg>
      </div>

      {}
      <div className="pointer-events-none absolute top-[20%] left-[-80px] h-64 w-64 rounded-lg opacity-30 blur-3xl" style={{
      background: "var(--secondary)",
      animation: "purefloat1 14s ease-in-out infinite"
    }} />
      <div className="pointer-events-none absolute bottom-[18%] right-[42%] h-48 w-48 rounded-lg opacity-25 blur-3xl" style={{
      background: "var(--accent)",
      animation: "purefloat2 18s ease-in-out infinite"
    }} />

      <style>{`
        @keyframes purefloat1 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(40px, -30px); }
        }
        @keyframes purefloat2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-30px, 40px); }
        }
        @keyframes purescroll {
          0% { transform: scaleY(0); transform-origin: top; }
          50% { transform: scaleY(1); transform-origin: top; }
          50.01% { transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }
      `}</style>

      <div className="relative mx-auto flex min-h-[92vh] max-w-[1400px] flex-col px-6 pt-28 pb-20 md:px-10 lg:flex-row lg:items-center lg:gap-8 lg:px-12 lg:pt-32">
        {}
        <div ref={leftRef} className={`order-2 w-full transition-all duration-1000 ease-out lg:order-1 lg:w-[58%] ${leftVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {}
          <div className="flex items-center gap-4">
            <span className="block h-px w-12 bg-secondary" />
            <span className="font-mono text-[11px] tracking-[0.18em] text-muted-foreground uppercase">
              — LOVEJOY COMPANY / EST. 2024
            </span>
          </div>

          {}
          <h1 className="mt-8 font-default font-light text-primary leading-[0.95] tracking-tight" style={{
          fontSize: "clamp(48px, 9vw, 140px)"
        }}>
            Clarity in every{" "}
            <span className="italic font-normal text-primary/95" style={{
            fontSize: "clamp(54px, 10vw, 156px)"
          }}>
              filing
            </span>{" "}
            process.
          </h1>

          {}
          <p className="mt-10 max-w-[420px] font-default text-[18px] leading-[1.65] text-muted-foreground">
            We provide tax services tailored to individuals and businesses seeking accurate compliance and strategic financial planning. Our team guides you through complex requirements with transparency and proactive risk management.
          </p>

          {}
          <div className="mt-12 flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-8">
            <Button asChild size="lg" className="group h-12 rounded-lg bg-accent px-7 text-accent-foreground hover:bg-accent/90">
              <Link to="#" className="flex items-center gap-2">
                <span className="font-default text-[14px] tracking-wide">Get Started Today</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>

            <Link to="#" className="group inline-flex items-center gap-2 border-b border-foreground/40 pb-1 font-mono text-[11px] tracking-[0.22em] text-foreground uppercase transition-colors hover:border-foreground">
              Our Philosophy
            </Link>
          </div>

          {}
          <div className="mt-20 hidden items-center gap-3 lg:flex">
            <span className="font-mono text-[10px] tracking-[0.28em] text-muted-foreground uppercase">
              Scroll
            </span>
            <span className="block h-10 w-px bg-foreground/60" style={{
            animation: "purescroll 2.4s ease-in-out infinite"
          }} />
          </div>
        </div>

        {}
        <div ref={rightRef} className={`relative order-1 mb-12 w-full transition-all duration-1000 ease-out lg:order-2 lg:mb-0 lg:w-[42%] ${rightVisible ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"}`}>
          <div className="relative mx-auto aspect-[3/4] w-full max-w-[480px] lg:max-w-none lg:-mr-12 lg:h-[78vh] lg:aspect-auto">
            {}
            <div className="pointer-events-none absolute -top-3 -left-3 h-12 w-12 border-t border-l border-secondary" />
            <div className="pointer-events-none absolute -right-3 -bottom-3 h-12 w-12 border-r border-b border-secondary" />

            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_professional_office.png" alt="Professional tax advisor reviewing financial documents in a modern office setting" loading="lazy" decoding="async" className="h-full w-full object-cover" />

            {}
            {annotations.map((a, i) => <div key={i} data-index={i} className={`pointer-events-none absolute hidden items-center gap-3 md:flex ${a.side === "left" ? "right-full pr-0" : "left-full pl-0"}`} style={{
            top: a.top
          }}>
                {a.side === "left" ? <>
                    <span className="font-mono text-[10px] tracking-[0.22em] text-foreground uppercase whitespace-nowrap">
                      {a.label}
                    </span>
                    <span className="block h-px w-16 bg-foreground/50 lg:w-24" />
                    <span className="block h-2 w-2 rounded-lg border border-foreground/60" />
                  </> : <>
                    <span className="block h-2 w-2 rounded-lg border border-foreground/60" />
                    <span className="block h-px w-16 bg-foreground/50 lg:w-24" />
                    <span className="font-mono text-[10px] tracking-[0.22em] text-foreground uppercase whitespace-nowrap">
                      {a.label}
                    </span>
                  </>}
              </div>)}

            {}
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 md:hidden">
              {annotations.map((a, i) => <span key={i} data-index={i} className="font-mono text-[10px] tracking-[0.22em] text-muted-foreground uppercase">
                  · {a.label}
                </span>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}